import type { UrlField } from './codecs';

/** A page's URL fields by name, declared once at module level. */
export type UrlSchema = Record<string, UrlField<any>>;

/** The values a schema reads. */
export type UrlValues<S extends UrlSchema> = {
  [K in keyof S]: S[K] extends UrlField<infer T> ? T : never;
};

function allKeys(schema: UrlSchema): Set<string> {
  return new Set(Object.values(schema).flatMap((field) => field.keys));
}

/** Every field's value: the URL's when present and valid, otherwise the default. */
export function readUrlState<S extends UrlSchema>(schema: S, params: URLSearchParams): UrlValues<S> {
  const values = {} as Record<string, unknown>;
  for (const [name, field] of Object.entries(schema)) {
    values[name] = field.read(params) ?? field.getDefault();
  }
  return values as UrlValues<S>;
}

/**
 * The URL for `values`: keys the schema doesn't own stay as they are, in their order; then each
 * field in schema order under its canonical key, left out when it's the default. Old key names and
 * invalid values drop out.
 */
export function writeUrlState<S extends UrlSchema>(
  schema: S,
  base: URLSearchParams,
  values: UrlValues<S>
): URLSearchParams {
  const owned = allKeys(schema);
  const next = new URLSearchParams();
  for (const [key, value] of base) if (!owned.has(key)) next.append(key, value);
  for (const [name, field] of Object.entries(schema)) {
    const value = (values as Record<string, unknown>)[name];
    if (!field.equals(value, field.getDefault())) field.write(next, value);
  }
  return next;
}

/** Whether changing from `prev` to `next` adds a history entry: a `push` field changed. */
export function changesPush<S extends UrlSchema>(schema: S, prev: UrlValues<S>, next: UrlValues<S>) {
  return Object.entries(schema).some(
    ([name, field]) =>
      field.history === 'push' &&
      !field.equals((prev as Record<string, unknown>)[name], (next as Record<string, unknown>)[name])
  );
}

/** The remembered fields only, as a query string (decision 24 a). '' when they're all default. */
export function rememberedQuery<S extends UrlSchema>(schema: S, values: UrlValues<S>): string {
  return writeUrlState(rememberedSchema(schema), new URLSearchParams(), values).toString();
}

function rememberedSchema(schema: UrlSchema): UrlSchema {
  return Object.fromEntries(Object.entries(schema).filter(([, field]) => field.remember));
}

/**
 * The URL to show for a bare link to a page that remembers its last view, or null to keep the
 * URL as it is. A URL with any remembered key sets the whole view itself (resolution 10);
 * otherwise the stored view goes on top of what it does have (a search, a page, a record).
 */
export function restoreLastView<S extends UrlSchema>(
  schema: S,
  params: URLSearchParams,
  stored: string | null
): URLSearchParams | null {
  if (!stored) return null;
  const remembered = rememberedSchema(schema);
  if (Object.keys(remembered).length === 0) return null;
  if ([...allKeys(remembered)].some((key) => params.has(key))) return null;
  // Through the codecs, so a stored value that's no longer valid falls back to the default.
  const storedValues = readUrlState(remembered, new URLSearchParams(stored));
  const values = { ...readUrlState(schema, params), ...storedValues } as UrlValues<S>;
  const next = writeUrlState(schema, params, values);
  return next.toString() === params.toString() ? null : next;
}

/**
 * A link to a page with these values (the rest default): `urlHref('/wholesale/price-lists',
 * PRICE_LISTS_URL, { vendorIds })`. Each page exports its own builder made with this.
 */
export function urlHref<S extends UrlSchema>(
  pathname: string,
  schema: S,
  values: Partial<UrlValues<S>>,
  extra?: URLSearchParams
): string {
  const full = { ...readUrlState(schema, new URLSearchParams()), ...values } as UrlValues<S>;
  const query = writeUrlState(schema, extra ?? new URLSearchParams(), full).toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * The query for a data cache key: every field but presentation ones (`cols`), canonical, so old
 * key names and default values give the same key.
 */
export function urlDataKey<S extends UrlSchema>(schema: S, values: UrlValues<S>): string {
  const data = Object.fromEntries(Object.entries(schema).filter(([, field]) => !field.presentation));
  return writeUrlState(data, new URLSearchParams(), values).toString();
}
